import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../constants/providers/AuthProvider';

interface UserCompany {
  company_id: string;
  company_name: string;
  company_logo?: string | null;
  role: string;
}

interface SidebarDataContextType {
  companies: UserCompany[];
  currentCompany: UserCompany | null;
  userRole: string | null;
  loading: boolean;
  expandedItems: Set<string>;
  scrollPosition: number;
  setCurrentCompany: (company: UserCompany | null) => void;
  setUserRole: (role: string | null) => void;
  setExpandedItems: (items: Set<string>) => void;
  setScrollPosition: (position: number) => void;
  handleSwitchCompany: (company: UserCompany) => Promise<void>;
  refreshSidebarData: () => Promise<void>;
}

const SidebarDataContext = createContext<SidebarDataContextType | undefined>(undefined);

export const SidebarDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [companies, setCompanies] = useState<UserCompany[]>([]);
  const [currentCompany, setCurrentCompany] = useState<UserCompany | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [scrollPosition, setScrollPosition] = useState(0);

  const { user: authUser } = useAuth();
  const authUserId = authUser?.id ?? null;

  // This provider stays mounted across sign-in/sign-out (see
  // AuthenticatedLayoutWrapper), so a request started for one user can finish
  // after the session has changed. Results are committed only while the user
  // they were fetched for is still the current one.
  const currentUserIdRef = useRef<string | null>(authUserId);
  currentUserIdRef.current = authUserId;

  const fetchSidebarData = useCallback(async () => {
    const requestUserId = authUserId;
    const isCurrent = () => currentUserIdRef.current === requestUserId;

    if (!requestUserId) {
      setCompanies([]);
      setCurrentCompany(null);
      setUserRole(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data: relationships } = await supabase
        .from('user_company_relationships')
        .select('company_id, role_id')
        .eq('user_id', requestUserId);

      if (!isCurrent()) return;

      if (!relationships || relationships.length === 0) {
        setCompanies([]);
        setCurrentCompany(null);
        setUserRole(null);
        return;
      }

      const companyIds = relationships.map(r => r.company_id);
      const { data: companiesData } = await supabase
        .from('companies')
        .select('id, name, logo_url')
        .in('id', companyIds);

      if (!isCurrent()) return;

      const userCompanies: UserCompany[] = relationships.map(r => ({
        company_id: r.company_id,
        company_name: companiesData?.find(c => c.id === r.company_id)?.name || 'Unknown Gym',
        company_logo: companiesData?.find(c => c.id === r.company_id)?.logo_url,
        role: r.role_id ? 'owner' : 'member', // Simplified for now
      }));

      setCompanies(userCompanies);

      const selectedCompany = userCompanies[0]; // Default to first
      setCurrentCompany(selectedCompany);
      setUserRole(selectedCompany?.role || null);

    } catch (err) {
      console.error('Error fetching sidebar data:', err);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [authUserId]);

  // Drop the previous user's data the moment the user changes, then refetch.
  useEffect(() => {
    setCompanies([]);
    setCurrentCompany(null);
    setUserRole(null);
    fetchSidebarData();
  }, [fetchSidebarData]);

  const handleSwitchCompany = useCallback(async (company: UserCompany) => {
    setCurrentCompany(company);
    setUserRole(company.role);
  }, []);

  const value: SidebarDataContextType = {
    companies,
    currentCompany,
    userRole,
    loading,
    expandedItems,
    scrollPosition,
    setCurrentCompany,
    setUserRole,
    setExpandedItems,
    setScrollPosition,
    handleSwitchCompany,
    refreshSidebarData: fetchSidebarData,
  };

  return (
    <SidebarDataContext.Provider value={value}>
      {children}
    </SidebarDataContext.Provider>
  );
};

export const useSidebarData = () => {
  const context = useContext(SidebarDataContext);
  if (context === undefined) {
    throw new Error('useSidebarData must be used within a SidebarDataProvider');
  }
  return context;
};
